'use client';

import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DatePicker, SmartInput } from '@web/ui';

import styles from './TimelineDeliverableEditor.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';
import type { GanttTaskChange } from '@web/ui';
import type { Dayjs } from 'dayjs';

type TimelineDeliverableEditorProps = {
  deliverables: JobDeliverable[];
  summaryStartDateIso?: string | null;
  disabled?: boolean;
  onChange: (change: GanttTaskChange) => void;
};

type DeliverableDraft = {
  startDate: string;
  durationDays: number;
};

const toDraftFromDeliverable = (
  deliverable: JobDeliverable,
  summaryStartDateIso?: string | null,
): DeliverableDraft => {
  const fallbackStart = summaryStartDateIso
    ? dayjs(summaryStartDateIso).startOf('day')
    : dayjs().startOf('day');
  const start = dayjs(deliverable.start_date ?? fallbackStart).startOf('day');
  const explicitDue = deliverable.due_date ?? deliverable.end_date ?? null;
  const due = explicitDue
    ? dayjs(explicitDue).startOf('day')
    : start.add(Math.max(deliverable.duration ?? 1, 1), 'day');

  const safeStart = start.isValid() ? start : fallbackStart;
  const safeDue =
    due.isValid() && due.isAfter(safeStart) ? due : safeStart.add(1, 'day');
  const safeDuration = Math.max(safeDue.diff(safeStart, 'day'), 1);

  return {
    startDate: safeStart.format('YYYY-MM-DD'),
    durationDays: safeDuration,
  };
};

export function TimelineDeliverableEditor({
  deliverables,
  summaryStartDateIso,
  disabled = false,
  onChange,
}: TimelineDeliverableEditorProps) {
  const [selectedDeliverableId, setSelectedDeliverableId] = useState<
    string | null
  >(null);
  const [draftsByDeliverableId, setDraftsByDeliverableId] = useState<
    Record<string, DeliverableDraft>
  >({});
  const durationSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (durationSaveTimerRef.current) {
        clearTimeout(durationSaveTimerRef.current);
      }
    };
  }, []);

  const deliverableOptions = useMemo(
    () =>
      deliverables.map((deliverable) => ({
        value: deliverable.id,
        label: deliverable.name,
      })),
    [deliverables],
  );

  if (deliverables.length === 0) {
    return null;
  }

  const selectedId =
    selectedDeliverableId &&
    deliverables.some((deliverable) => deliverable.id === selectedDeliverableId)
      ? selectedDeliverableId
      : deliverables[0]?.id;

  const selectedDeliverable =
    deliverables.find((deliverable) => deliverable.id === selectedId) ??
    deliverables[0];
  const baseDraft = toDraftFromDeliverable(
    selectedDeliverable,
    summaryStartDateIso,
  );
  const draft = draftsByDeliverableId[selectedId] ?? baseDraft;

  const updateDraft = (nextDraft: DeliverableDraft) => {
    setDraftsByDeliverableId((current) => ({
      ...current,
      [selectedId]: nextDraft,
    }));
  };

  const emitDraftChange = (nextDraft: DeliverableDraft) => {
    const endDate = dayjs(nextDraft.startDate)
      .startOf('day')
      .add(Math.max(nextDraft.durationDays, 1), 'day')
      .format('YYYY-MM-DD');

    onChange({
      taskId: selectedId,
      startDate: nextDraft.startDate,
      endDate,
      durationDays: nextDraft.durationDays,
    });
  };

  const handleStartDateChange = (value: Dayjs | null) => {
    if (disabled || !value) {
      return;
    }

    const nextStart = value.startOf('day');
    const nextDraft: DeliverableDraft = {
      startDate: nextStart.format('YYYY-MM-DD'),
      durationDays: Math.max(draft.durationDays, 1),
    };
    updateDraft(nextDraft);
    emitDraftChange(nextDraft);
  };

  const handleDurationChange = (value: number | null) => {
    if (disabled) {
      return;
    }

    const safeDuration = Math.max(1, Math.round(value ?? 1));
    const nextDraft: DeliverableDraft = {
      startDate: draft.startDate,
      durationDays: safeDuration,
    };
    updateDraft(nextDraft);

    if (durationSaveTimerRef.current) {
      clearTimeout(durationSaveTimerRef.current);
    }
    durationSaveTimerRef.current = setTimeout(() => {
      emitDraftChange(nextDraft);
    }, 250);
  };

  return (
    <div className={styles.timelineDeliverableEditor}>
      <SmartInput
        type="select"
        name="timeline-deliverable-selector"
        label="Deliverable"
        value={selectedId}
        options={deliverableOptions}
        readOnly={disabled}
        onChange={(value) => {
          setSelectedDeliverableId(String(value));
        }}
      />

      <DatePicker
        name="timeline-deliverable-start-date"
        label="Start Date"
        value={dayjs(draft.startDate)}
        readOnly={disabled}
        disabled={disabled}
        onChange={handleStartDateChange}
      />

      <SmartInput
        type="number"
        name="timeline-deliverable-duration"
        label="Duration"
        value={draft.durationDays}
        min={1}
        readOnly={disabled}
        onChange={handleDurationChange}
      />
    </div>
  );
}
